import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAuditorBundle, type AuditorBundle } from "@/lib/api";

/**
 * What the external auditor sees. No login, no session — the token in the URL
 * is the credential — and nothing on this page can write anything. It sits
 * outside ProtectedRoute and outside AppLayout, so the portal's navigation
 * never appears.
 */
type Section = "controls" | "operating" | "policies" | "soa" | "risks" | "audit";

const SECTIONS: { key: Section; label: string }[] = [
  { key: "controls", label: "Controls" },
  { key: "operating", label: "Operating record" },
  { key: "policies", label: "Policies" },
  { key: "soa", label: "Statement of Applicability" },
  { key: "risks", label: "Risks" },
  { key: "audit", label: "Audit & findings" },
];

export function AuditorViewPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [section, setSection] = useState<Section>("controls");
  const { data, isLoading, error } = useQuery({
    queryKey: ["auditor-bundle", token],
    queryFn: () => getAuditorBundle(token),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="grid h-screen place-items-center bg-bg text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="grid h-screen place-items-center bg-bg px-6 text-center">
        <div className="max-w-md">
          <h1 className="page-title">Link not valid</h1>
          <p className="mt-2 text-sm text-text-secondary">
            {(error as Error | undefined)?.message ??
              "This auditor link is not valid, has expired, or was revoked."}
          </p>
          <p className="mt-4 text-xs text-text-muted">
            Ask your contact at Vernias to issue a new one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <header className="border-b border-border bg-bg-card">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-6 py-4">
          <img src="/logo.png" alt="" className="h-7 w-7 object-contain" />
          <div>
            <h1 className="font-display text-base font-semibold text-text-heading">
              Vernias — compliance record
            </h1>
            <p className="text-xs text-text-muted">
              {data.share.label} · read-only · access expires {data.share.expiresOn}
            </p>
          </div>
          <span className="ml-auto text-xs text-text-muted">
            Generated {new Date(data.share.generatedAt).toLocaleString()}
          </span>
        </div>
        <nav className="mx-auto flex max-w-6xl flex-wrap gap-1 px-6">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                section === s.key
                  ? "border-accent text-text-heading"
                  : "border-transparent text-text-secondary hover:text-text-heading"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {section === "controls" && <Controls data={data} />}
        {section === "operating" && <Operating data={data} />}
        {section === "policies" && <Policies data={data} />}
        {section === "soa" && <Soa data={data} />}
        {section === "risks" && <Risks data={data} />}
        {section === "audit" && <AuditSection data={data} />}
      </main>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="page-title mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-token-lg border border-border bg-bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
            {head.map((h) => (
              <th key={h} className="px-4 py-2.5 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Controls({ data }: { data: AuditorBundle }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card title={`Controls (${data.controls.length})`}>
      <Table head={["Ref", "Control", "Owner", "Frequency", "Satisfies", "Status"]}>
        {data.controls.map((c) => (
          <>
            <tr
              key={c.ref}
              className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
              onClick={() => setOpen(open === c.ref ? null : c.ref)}
            >
              <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">{c.ref}</td>
              <td className="px-4 py-2.5 text-text-heading">{c.name}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">{c.owner ?? "—"}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                {c.frequency.replace("_", " ")}
              </td>
              <td className="px-4 py-2.5 text-xs text-text-muted">{c.mappings.join(", ")}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">{c.status}</td>
            </tr>
            {open === c.ref && (
              <tr key={`${c.ref}-detail`} className="border-b border-border-light bg-bg-secondary">
                <td colSpan={6} className="px-4 py-3">
                  <p className="mb-2 whitespace-pre-wrap text-sm text-text-secondary">
                    {c.description}
                  </p>
                  <h4 className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
                    How we prove it operated
                  </h4>
                  <p className="mb-2 whitespace-pre-wrap text-sm text-text-secondary">
                    {c.testProcedure || "—"}
                  </p>
                  {c.justification && (
                    <p className="text-xs text-text-muted">
                      Applicability: {c.applicability} — {c.justification}
                    </p>
                  )}
                  {c.auditorNotes && (
                    <p className="mt-2 text-xs italic text-text-muted">{c.auditorNotes}</p>
                  )}
                </td>
              </tr>
            )}
          </>
        ))}
      </Table>
    </Card>
  );
}

function Operating({ data }: { data: AuditorBundle }) {
  if (data.periods.length === 0) {
    return <Card title="Operating record">No observation period defined.</Card>;
  }
  return (
    <>
      {data.periods.map((p) => (
        <Card key={p.name} title={`${p.name} (${p.startsOn} → ${p.endsOn})`}>
          <Table head={["Control", "Owner", "Occurrences"]}>
            {p.controls.map((r) => (
              <tr key={r.ref} className="border-b border-border-light">
                <td className="px-4 py-2.5">
                  <span className="font-medium text-text-heading">{r.ref}</span>
                  <span className="ml-2 text-text-secondary">{r.name}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                  {r.owner ?? "—"}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    {r.occurrences.map((o) => (
                      <span
                        key={o.dueOn}
                        title={`due ${o.dueOn}${o.occurredOn ? `, operated ${o.occurredOn}` : ""} · ${o.evidenceCount} evidence item(s)`}
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          o.stage === "closed"
                            ? "bg-success/15 text-success"
                            : o.result === "exception"
                              ? "bg-warning/15 text-warning"
                              : "border border-border text-text-secondary"
                        }`}
                      >
                        {o.dueOn.slice(5)} · {o.evidenceCount}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      ))}
      <p className="text-xs text-text-muted">
        {data.share.evidenceDownloadable
          ? "Evidence artefacts are downloadable on this link — ask for the sample you want."
          : "This link shows evidence counts and dates. Artefacts themselves are shared on request."}
      </p>
    </>
  );
}

function Policies({ data }: { data: AuditorBundle }) {
  return (
    <Card title={`Policies (${data.policies.length})`}>
      <Table head={["Policy", "Kind", "Owner", "Version", "Effective", "Acknowledged", "Status"]}>
        {data.policies.map((p) => (
          <tr key={p.name} className="border-b border-border-light">
            <td className="px-4 py-2.5 font-medium text-text-heading">{p.name}</td>
            <td className="px-4 py-2.5 capitalize text-text-secondary">{p.kind}</td>
            <td className="px-4 py-2.5 text-text-secondary">{p.owner ?? "—"}</td>
            <td className="px-4 py-2.5 text-text-secondary">{p.version ? `v${p.version}` : "—"}</td>
            <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
              {p.effectiveFrom ?? "—"}
            </td>
            <td className="px-4 py-2.5 text-text-secondary">{p.acknowledged}</td>
            <td className="px-4 py-2.5 text-text-secondary">{p.status}</td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function Soa({ data }: { data: AuditorBundle }) {
  return (
    <Card title={`Statement of Applicability (${data.soa.length} Annex A controls)`}>
      <Table head={["Ref", "Control", "Applicable", "Implemented by", "Risks", "Justification"]}>
        {data.soa.map((e) => (
          <tr key={e.ref} className="border-b border-border-light">
            <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">{e.ref}</td>
            <td className="px-4 py-2.5 text-text-secondary">{e.title}</td>
            <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
              {e.applicability === "applicable" ? "Yes" : e.applicability === "not_applicable" ? "Excluded" : "—"}
            </td>
            <td className="px-4 py-2.5 text-xs text-text-muted">{e.controls.join(", ") || "—"}</td>
            <td className="px-4 py-2.5 text-xs text-text-muted">{e.risks.join(", ") || "—"}</td>
            <td className="px-4 py-2.5 text-text-secondary">{e.justification ?? "—"}</td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function Risks({ data }: { data: AuditorBundle }) {
  return (
    <Card title={`Risk register (${data.risks.length})`}>
      <Table head={["Ref", "Risk", "Owner", "Inherent", "Treatment", "Residual", "Status"]}>
        {data.risks.map((r) => (
          <tr key={r.ref} className="border-b border-border-light">
            <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">{r.ref}</td>
            <td className="px-4 py-2.5 text-text-heading">{r.title}</td>
            <td className="px-4 py-2.5 text-text-secondary">{r.owner ?? "—"}</td>
            <td className="px-4 py-2.5 text-text-secondary">{r.inherentScore ?? "—"}</td>
            <td className="px-4 py-2.5 capitalize text-text-secondary">{r.treatment ?? "—"}</td>
            <td className="px-4 py-2.5 text-text-secondary">{r.residualScore ?? "—"}</td>
            <td className="px-4 py-2.5 text-text-secondary">{r.status}</td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function AuditSection({ data }: { data: AuditorBundle }) {
  return (
    <>
      {data.programmes.map((p) => (
        <Card key={p.name} title={p.name}>
          <p className="mb-3 text-sm text-text-secondary">
            {p.startsOn} → {p.endsOn} · control coverage {p.coverage}
            {p.objectives ? ` · ${p.objectives}` : ""}
          </p>
          <Table head={["Engagement", "Auditor", "Conducted", "Scope", "Findings"]}>
            {p.engagements.map((e) => (
              <tr key={e.title} className="border-b border-border-light">
                <td className="px-4 py-2.5 font-medium text-text-heading">{e.title}</td>
                <td className="px-4 py-2.5 text-text-secondary">{e.auditor ?? "—"}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                  {e.conductedOn ?? "planned"}
                </td>
                <td className="px-4 py-2.5 text-xs text-text-muted">
                  {e.controls.join(", ") || e.scope}
                </td>
                <td className="px-4 py-2.5 text-text-secondary">{e.findingCount}</td>
              </tr>
            ))}
          </Table>
        </Card>
      ))}

      <Card title={`Findings (${data.findings.length})`}>
        {data.findings.length === 0 ? (
          <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
            No findings recorded.
          </p>
        ) : (
          <div className="space-y-3">
            {data.findings.map((f) => (
              <div key={f.ref} className="rounded-token-lg border border-border bg-bg-card p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium text-text-heading">
                    {f.ref} — {f.title}
                  </span>
                  <span className="text-xs text-text-muted">
                    {f.type.replace("_", " ")} · {f.source.replace("_", " ")} · raised {f.raisedOn} ·{" "}
                    {f.closedOn ? `closed ${f.closedOn}` : f.stage.replace("_", " ")}
                    {f.control ? ` · ${f.control}` : ""}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-text-secondary">
                  {f.description}
                </p>
                {f.actions.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {f.actions.map((a, i) => (
                      <li key={i} className="border-l-2 border-border pl-3 text-sm">
                        <div className="text-text-heading">{a.action}</div>
                        {a.rootCause && (
                          <div className="text-xs text-text-secondary">
                            <span className="text-text-muted">Root cause:</span> {a.rootCause}
                          </div>
                        )}
                        <div className="text-xs text-text-muted">
                          target {a.targetDate ?? "—"} · completed {a.completedOn ?? "—"} ·{" "}
                          {a.verifiedOn ? (
                            <span className="text-success">verified effective {a.verifiedOn}</span>
                          ) : (
                            "not yet verified"
                          )}
                        </div>
                        {a.verificationNote && (
                          <div className="text-xs italic text-text-muted">“{a.verificationNote}”</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
